import React, { useRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  maxMobile,
  maxWidth, maxWidth1300, primaryFont, retina, secondaryFont, wMedium,
} from 'Utils/utilsStyle';
import classNames from 'classnames';
import { ChartsData, ChartCoordintes } from 'Types/CompaniesTypes';
import type { GradientColorStopObject } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { usePrevious } from 'Utils/hooks/usePrevious';
import { useHighchartsModule } from 'Utils/hooks/useHighchartsModule';
import ChartBaseConfig from 'Utils/AreaChartConfig';
import { Spin } from 'antd';
import { IsTablet } from 'Utils/UtilityFunctions';
import { translateSkipAreas } from 'Utils/Constants';

type Props = {
  chartHeading: string,
  showTabs?: boolean,
  width?: string,
  height?: string,
  heightSm?: string,
  defaultTab?: string,
  chartsData?: ChartsData,
  showSubHeading?: boolean,
  className?: string,
} & typeof defaultProps;

const defaultProps = {
  showTabs: true,
  width: '100%',
  height: '128px',
  heightSm: '128px',
  defaultTab: 'annual',
  showSubHeading: false,
  chartsData: {
    annualData: [] as ChartCoordintes[],
    dailyData: [] as ChartCoordintes[],
  },
  className: '',
};

interface WrapperProps {
  height: string,
  width: string,
  heightSm: string,
}

const StyledWrapper = styled.div<WrapperProps>`
  /* min-height: ${(props) => props.height}; */
  min-height: 92%;
  width: ${(props) => props.width};
  display: inline-block;
  background-color: white !important;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding:10px;
  box-shadow: 0px 0px 24px rgb(0 0 0 / 10%);
  &.resRes {
    @media all and (max-width: ${maxWidth}) , ${retina} {
     /* min-height: ${(props) => props.heightSm}; */
    }
  }
  .heading{
    color: #001823;
    font-weight: ${wMedium};
    font-family: ${secondaryFont};
    font-size: 16px;
    line-height: 14px;
  }
  .subHeading {
    font-family: ${primaryFont};
    font-size: 16px;
    line-height: 34px;
    color: RGB(103, 105, 106);
  }
  .c-charts-action {
    .tabs-item {
      margin-right: 0px;
      padding: 3px 8px;
      font-size: 13px;
      @media all and (max-width: ${maxWidth}) , ${retina} {
        font-size: 9px;  
      }
      @media all and (max-width: ${maxWidth1300}){
        margin-right: 0px !important;
      }
      &:first-child {
        border-radius: 8px 0px 0px 8px !important;
      }
       &:last-child {
        border-radius: 0px 8px 8px 0px !important;
      }
    }
  }
  .transparent{
    opacity:0;
  }
  &.finacial-chart{
    .heading{
      font-size:24px;
      color:#424B50;
      margin: 20px 25px 5px 25px;
      float: left;
      @media all and (max-width: ${maxWidth}) , ${retina} {
        font-size: 18px;
        margin: 10px 0px 0px 15px;
      }
    }
    .subHeading {
      margin: 0 25px 0 25px;
      float: left;
      @media all and (max-width: ${maxWidth}) , ${retina} {
        font-size: 16px;
        margin: 2px 0px 0px 15px;
      }
    }
    &.resRes {
      @media all and (max-width: ${maxMobile}) {
        margin-top: 20px;
      }
    }
  }
`;

type ConfigObj = {
  seriesName: string
  label: string,
  stops: GradientColorStopObject[]
  lineColor: string
  markerColor: string
};

type SeriesNames = {
  annual: ConfigObj
  daily: ConfigObj
};

const seriesNames: SeriesNames = {
  annual: {
    seriesName: 'Annual Data',
    label: 'Annual',
    stops: [
      [0, '#1ec052'],
      [1, 'rgba(30, 192, 82, 0.3)'],
    ],
    lineColor: 'rgba(30, 192, 82, 0.1)',
    markerColor: '#1ec052',
  },
  daily: {
    seriesName: 'Daily Data',
    label: 'Daily',
    stops: [
      [0, '#53acee'],
      [1, 'rgba(83, 172, 238, 0.3)'],
    ],
    lineColor: 'rgba(83, 172, 238, 0.1)',
    markerColor: '#53acee',
  },
};

const StockChart = function StockChart(props: Props) {
  const {
    chartHeading, showTabs, width, height, defaultTab,
    chartsData, showSubHeading, className, heightSm,
  } = props;
  const chartComponentRef = useRef<HighchartsReact.RefObject>(null);
  const highcharts = useHighchartsModule();
  const [activeTab, setActiveTab] = useState<string>('annual');
  const [series, setSeries] = useState<number[][]>([]);
  const isTab = IsTablet();
  const previousTab = usePrevious(activeTab);

  const changeSeries = (activeChartsData: ChartCoordintes[]) => {
    const seriesCopy = [];
    for (let i = 0; i < activeChartsData.length; i += 1) {
      seriesCopy.push(
        [
          activeChartsData[i].x * 1000,
          activeChartsData[i].y,
        ],
      );
    }
    setSeries(seriesCopy);
  };

  useEffect(() => {
    if (activeTab !== defaultTab) {
      setActiveTab(defaultTab);
    }
  }, [defaultTab]);

  useEffect(() => {
    const activeChartsData: ChartCoordintes[] = chartsData[`${activeTab}Data` as keyof ChartsData];
    if (series.length !== activeChartsData.length) {
      changeSeries(activeChartsData);
    }
  }, [chartsData]);

  useEffect(() => {
    const activeChartsData: ChartCoordintes[] = chartsData[`${activeTab}Data` as keyof ChartsData];
    if (previousTab !== activeTab) {
      changeSeries(activeChartsData);
    }
  }, [activeTab]);

  const handleClick = (type: string) => {
    setActiveTab(type);
  };

  const returnChartTypeButton = (type = 'annual') => (
    <span
      className={classNames('tabs-item', { active: activeTab === type })}
      onClick={() => handleClick(type)}
      role="button"
      aria-label={seriesNames[type as keyof typeof seriesNames].label}
      onKeyPress={(e) => (e.key === 'enter' ? setActiveTab(type) : null)}
      tabIndex={0}
      style={{
        cursor: 'pointer',
      }}
    >
      {seriesNames[type as keyof typeof seriesNames].label}
    </span>
  );

  return (
    <StyledWrapper
      height={height}
      width={width}
      className={classNames(className, { resRes: isTab })}
      heightSm={heightSm}
    >
      <div className="d-flex align-items-center justify-content-between">
        <div>
          <span className="heading">{chartHeading}</span>
          {
            showSubHeading ? (
              <>
                <br />
                <span className="subHeading">Click and drag to zoom in</span>
              </>
            ) : null
          }
        </div>

        <div className={`tabs-wrapper c-charts-action  ${!showTabs ? 'transparent' : ''} `}>
          {returnChartTypeButton('annual')}
          {returnChartTypeButton('daily')}
        </div>
      </div>
      <div className={translateSkipAreas}>
        {highcharts ? (
          <HighchartsReact
            highcharts={highcharts}
            options={ChartBaseConfig(
              seriesNames[activeTab as keyof typeof seriesNames],
              series,
              isTab ? `${heightSm}` : `${height}`,
            )}
            ref={chartComponentRef}
          />
        ) : <Spin />}
      </div>
    </StyledWrapper>
  );
};

StockChart.defaultProps = defaultProps;

export default StockChart;
